import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, TrendingUp, Users, Sparkles, LogIn, Video, Mic, Smile, Send, X } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import CommunityPost from '../components/consultant/CommunityPost';
import Navbar from '../components/Navbar';
import PublicChatPreview from '../components/PublicChatPreview';

export default function PublicCommunity() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'trending'>('all');
  const [showSignupModal, setShowSignupModal] = useState(false);

  useEffect(() => {
    fetchPublicPosts();
  }, []);

  const fetchPublicPosts = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('consultant_community_posts')
        .select('*')
        .eq('visibility', 'public')
        .order('created_at', { ascending: false });

      if (filter === 'trending') {
        query = query.order('likes_count', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filter === 'trending') {
      fetchPublicPosts();
    }
  }, [filter]);

  const handlePostComposerClick = () => {
    setShowSignupModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <Navbar />

      <main className="pt-32 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 text-black">
              Workday Community Connect
            </h1>
            <p className="text-gray-600">
              Explore insights and updates from Workday consultants
            </p>
          </div>

          <div className="flex items-center space-x-2 mb-6 p-1 rounded-lg bg-white">
            <button
              onClick={() => setFilter('all')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                filter === 'all'
                  ? 'bg-[#40E0D0] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">All Posts</span>
            </button>
            <button
              onClick={() => setFilter('trending')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                filter === 'trending'
                  ? 'bg-[#40E0D0] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              <span className="font-medium">Trending</span>
            </button>
          </div>

          <div className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#40E0D0]"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 rounded-xl bg-white">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold mb-2 text-gray-900">
                  No public posts yet
                </h3>
                <p className="text-gray-600">
                  Check back soon for updates from our consultant community!
                </p>
              </div>
            ) : (
              posts.map((post) => (
                <CommunityPost
                  key={post.id}
                  post={post}
                  onDelete={() => {}}
                  onUpdate={() => {}}
                  theme="light"
                  readOnly={true}
                  onReadOnlyAction={() => setShowSignupModal(true)}
                />
              ))
            )}
          </div>
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div
            onClick={handlePostComposerClick}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center space-x-2">
                <div
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-100 text-gray-600 transition-colors"
                >
                  <ImageIcon className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">Photo</span>
                </div>

                <div
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-100 text-gray-600 transition-colors"
                >
                  <Video className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium">Video</span>
                </div>

                <div
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-100 text-gray-600 transition-colors"
                >
                  <Mic className="w-5 h-5 text-purple-500" />
                  <span className="text-sm font-medium">Podcast</span>
                </div>

                <div
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-100 text-gray-600 transition-colors"
                >
                  <Smile className="w-5 h-5 text-yellow-500" />
                </div>
              </div>

              <button
                type="button"
                className="flex items-center space-x-2 px-6 py-2 bg-[#40E0D0] hover:bg-[#35c5b5] text-white font-semibold rounded-lg transition-colors"
              >
                <Send className="w-4 h-4" />
                <span>Post</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <PublicChatPreview onSignupPrompt={() => setShowSignupModal(true)} />

      {showSignupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setShowSignupModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#40E0D0] bg-opacity-10 rounded-full mb-4">
                <LogIn className="w-8 h-8 text-[#40E0D0]" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Join the Community
              </h2>

              <p className="text-gray-600 mb-6">
                Sign up as a consultant to create posts, engage with the community, and access exclusive opportunities.
              </p>

              <Link
                to="/customer-signup?type=consultant"
                className="inline-block w-full bg-[#40E0D0] hover:bg-[#35c5b5] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Sign Up Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
